// Every open task on one item assigned to one person. Has a URL of the form:
//    /me/item/:itemId/tasks/assigned?to=<name>
// reached from the assigned-name links on the task rows (Tasks.jsx).
//
// The name is the exact TaskPogoTask.assignedEmail string, read off the query
// string (see Urls.urlForTasksAssignedTo for why it is not a path segment).
// Same shape as Tasks.jsx -- breadcrumbs, a DataCard, TaskTable over a
// fetchItems -- with one list, no sort chooser (overdue first is the only
// order that makes sense for one person's to-do list) and no Add: a task is
// assigned from its own edit form.
//
// Share issues a public, revocable, LIVE link to this list, on the terms of the
// trip and logbook-trip shares. The button (and the modal, and every share
// query) sits behind shareAssignedTasksAvailable(), so a branch whose backend
// has not deployed the model shows this page without it.

import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DataCard, CardHeader, useStatusMessage } from '../../components';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import { TaskTable } from './TaskTable';
import { useAssignedToTaskQuery } from './TaskQuery';
import ShareAssignedTasksModal from './ShareAssignedTasksModal';
import { bucketNameAll, urlForTasks } from '../../fleet-shared/Urls.mjs';
import { hasAssignee } from '../../fleet-shared/TaskAssigned.mjs';
import { shareAssignedTasksAvailable } from '../../utilities/shareAssignedTasks.mjs';
import { useDocumentTitle } from '../../utilities/useDocumentTitle';
import { useTaskPhotos } from './useTaskPhotos';
import { useTaskViewPrefs } from './useTaskViewPrefs';
import { TaskViewControls } from './TaskViewControls';
import PhotoErrorAlert from '../logbook/PhotoErrorAlert';

const AssignedTasks = () => {
  const { itemId } = useParams();
  const [searchParams] = useSearchParams();
  const assignee = searchParams.get('to') ?? '';
  const assigned = hasAssignee({ assignedEmail: assignee });

  const [item, setItem] = useState(null);
  const [entries, setEntries] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [StatusMessage] = useStatusMessage();

  const fetchItems = useAssignedToTaskQuery(itemId, assignee, setItem);

  // One listing of `${itemId}/task/` for the page; see Tasks.jsx. The listing
  // spans every task on the item, including ones this list does not show -- the
  // prefix cannot express an assignee, and that is the documented cost, not a
  // bug to filter out.
  const [viewPrefs, setViewPref] = useTaskViewPrefs();
  const { photosByEntry, photosError, openPhoto } = useTaskPhotos(itemId, {
    enabled: viewPrefs.showPhotos,
  });

  // A new `?to=` is a new list: drop the old rows so the scroll restarts rather
  // than appending one person's tasks under another's.
  useEffect(() => {
    setEntries(null);
  }, [assignee]);

  useDocumentTitle(assigned ? `Assigned to ${assignee} - TaskPogo` : 'Assigned Tasks - TaskPogo');

  const shareTapped = useCallback(() => setShowShare(true), []);
  const onHide = useCallback(() => setShowShare(false), []);

  const canShare = shareAssignedTasksAvailable() && assigned;

  return (
    <div className="pb-2">
      <ItemBreadcrumbs
        item={item}
        extras={[{ name: 'Tasks', url: urlForTasks(itemId, bucketNameAll) }]}
      />
      <StatusMessage />

      {canShare && (
        <ShareAssignedTasksModal
          show={showShare}
          onHide={onHide}
          itemId={itemId}
          assignee={assignee}
          itemName={item?.name}
        />
      )}

      <DataCard
        header={
          <CardHeader
            text={assigned ? `Assigned to ${assignee}` : 'Assigned tasks'}
            belowText={
              <>
                {item?.name ? <div>{item.name}</div> : null}
                <TaskViewControls
                  showPhotos={viewPrefs.showPhotos}
                  onShowPhotosChange={(next) => setViewPref('showPhotos', next)}
                />
              </>
            }
            shareTapped={canShare ? shareTapped : undefined}
          />
        }
        body={
          <div className="tasks-container">
            <PhotoErrorAlert error={photosError} show={viewPrefs.showPhotos} subject="tasks" />
            {assigned ? (
              <TaskTable
                key={assignee}
                fetchItems={fetchItems}
                entries={entries}
                setEntries={setEntries}
                tasksListBucket={bucketNameAll}
                sortOverdueFirst
                showPhotos={viewPrefs.showPhotos}
                photosByEntry={photosByEntry}
                onOpenPhoto={openPhoto}
              />
            ) : (
              <p className="text-body-secondary text-center mb-0">
                No one is named. Open a task list and click an assigned name to see that
                person&apos;s tasks.
              </p>
            )}
          </div>
        }
      />
    </div>
  );
};

export default AssignedTasks;
